import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  CalendarDays,
  CircleUser,
  Gavel,
  Heart,
  Home,
  LayoutGrid,
  ShoppingCart,
  Sparkles,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { useStoreTheme } from '@/lib/useStoreTheme'
import type { AuctionSummary, ServiceSummary, WishlistItem } from '@/shared/types'

const TABS = [
  { to: '/' as const, icon: Home, labelKey: 'home' as const, badge: false as const },
  { to: '/catalog' as const, icon: LayoutGrid, labelKey: 'catalog' as const, badge: false as const },
  { to: '/quiz' as const, icon: Sparkles, labelKey: 'quiz' as const, badge: false as const },
  { to: '/auctions' as const, icon: Gavel, labelKey: 'auctions' as const, badge: false as const },
  { to: '/services' as const, icon: CalendarDays, labelKey: 'services' as const, badge: false as const },
  { to: '/wishlist' as const, icon: Heart, labelKey: 'wishlist' as const, badge: 'wishlist' as const },
  { to: '/cart' as const, icon: ShoppingCart, labelKey: 'cart' as const, badge: 'cart' as const },
  { to: '/profile' as const, icon: CircleUser, labelKey: 'profile' as const, badge: false as const },
]

export function BottomNav() {
  const cartCount = useAppStore((s) => s.cartCount)
  const { location } = useRouterState()
  const currentPath = location.pathname
  const T = useT('nav')
  const theme = useStoreTheme()

  const { data: settings } = useQuery<{
    auctions_enabled: boolean
    services_enabled: boolean
    shop_name?: string
    home_page_enabled?: boolean
    quiz_enabled?: boolean
  }>({
    queryKey: ['public-settings'],
    queryFn: () => api.get('/settings'),
    staleTime: 300_000,
  })

  const servicesEnabled = Boolean(settings?.services_enabled)
  const auctionsEnabled = Boolean(settings?.auctions_enabled)
  const isMayParfum = theme.isMayParfum

  const { data: services } = useQuery<ServiceSummary[]>({
    queryKey: ['services-nav'],
    queryFn: () => api.get<ServiceSummary[]>('/services'),
    enabled: servicesEnabled,
    staleTime: 300_000,
  })

  const { data: auctions } = useQuery<AuctionSummary[]>({
    queryKey: ['auctions-nav'],
    queryFn: () => api.get<AuctionSummary[]>('/auctions'),
    enabled: auctionsEnabled,
    staleTime: 300_000,
  })
  const auctionsCount = auctions?.length ?? 0

  const { data: wishlist } = useQuery<WishlistItem[]>({
    queryKey: ['wishlist'],
    queryFn: () => api.get<WishlistItem[]>('/wishlist'),
  })
  const wishlistCount = wishlist?.length ?? 0

  // Priority-based tab selection strictly capped at max 5 tabs
  const visibleTabs = useMemo(() => {
    const tabs: Array<(typeof TABS)[number]> = []

    // 1. Home tab (if home page enabled)
    if (settings?.home_page_enabled) {
      const homeTab = TABS.find((t) => t.to === '/')
      if (homeTab) tabs.push(homeTab)
    }

    // 2. Catalog tab (always primary)
    const catalogTab = TABS.find((t) => t.to === '/catalog')
    if (catalogTab) tabs.push(catalogTab)

    // 3. Featured contextual slot (strictly 1 primary feature)
    if (settings?.quiz_enabled && isMayParfum) {
      const quizTab = TABS.find((t) => t.to === '/quiz')
      if (quizTab) tabs.push(quizTab)
    } else if (auctionsEnabled && auctionsCount > 0) {
      const auctionTab = TABS.find((t) => t.to === '/auctions')
      if (auctionTab) tabs.push(auctionTab)
    } else if (servicesEnabled && services && services.length > 0) {
      const servicesTab = TABS.find((t) => t.to === '/services')
      if (servicesTab) tabs.push(servicesTab)
    } else {
      const wishlistTab = TABS.find((t) => t.to === '/wishlist')
      if (wishlistTab) tabs.push(wishlistTab)
    }

    // 4. Cart tab (always primary)
    const cartTab = TABS.find((t) => t.to === '/cart')
    if (cartTab) tabs.push(cartTab)

    // 5. Profile tab (always primary)
    const profileTab = TABS.find((t) => t.to === '/profile')
    if (profileTab) tabs.push(profileTab)

    // Strict safety cap: maximum 5 tabs
    return tabs.slice(0, 5)
  }, [settings, isMayParfum, auctionsEnabled, auctionsCount, servicesEnabled, services])

  const navContainerStyle = theme.isDarkTheme
    ? 'app-bottom-fixed app-bottom-nav border-t border-[#2d3748] bg-[#1a202c]/95 shadow-[0_-10px_28px_rgba(0,0,0,0.4)] backdrop-blur-xl'
    : 'app-bottom-fixed app-bottom-nav border-t border-brand-200/70 bg-white/95 shadow-[0_-10px_28px_rgba(15,23,42,0.08)] backdrop-blur-xl'

  return (
    <nav className={navContainerStyle}>
      <ul
        className="grid gap-1 px-2 pt-1.5"
        style={{ gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))` }}
      >
        {visibleTabs.map(({ to, icon: Icon, labelKey, badge }) => {
          const active =
            to === '/'
              ? currentPath === '/'
              : currentPath === to || currentPath.startsWith(to + '/')
          const label = T[labelKey]
          const count = badge === 'cart' ? cartCount : badge === 'wishlist' ? wishlistCount : 0

          let activeTabClass = 'bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/80'
          let inactiveTabClass = 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
          let activeBarClass = 'bg-brand-500'
          let badgeClass = 'bg-brand-600 text-white'

          if (theme.isDarkTheme) {
            activeTabClass = 'bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30 font-semibold'
            inactiveTabClass = 'text-slate-400 hover:bg-[#242c38] hover:text-slate-200'
            activeBarClass = 'bg-amber-400'
            badgeClass = 'bg-[#c9a24b] text-[#17120f] font-bold shadow-xs'
          }

          return (
            <li key={to} className="min-w-0">
              <Link
                to={to}
                className={`relative flex h-14 w-full min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-1 text-center text-[11px] leading-none transition-colors ${
                  active ? activeTabClass : inactiveTabClass
                }`}
              >
                {active && (
                  <span className={`absolute left-3 right-3 top-1 h-0.5 rounded-full ${activeBarClass}`} />
                )}
                <span className="relative flex h-6 items-center justify-center">
                  <Icon className={`h-5 w-5 ${active ? 'stroke-[2.5]' : 'stroke-[1.5]'}`} />
                  {count > 0 && (
                    <span className={`absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[10px] ${badgeClass}`}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </span>
                <span
                  className={`max-w-full truncate ${active ? 'font-semibold' : 'font-medium'}`}
                >
                  {label}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
